import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StudentSidebar from '../components/studentsidebar';
import Navbar from '../components/studentnavbar';
import { useSidebar } from '../components/SidebarContext';
import { generateTest, createRazorpayOrder, verifyRazorpayPayment, checkPaymentStatus } from '../services/testService';

const GenerateTestPage = () => {
  const [studentId, setStudentId] = useState('');
  const [studentData, setStudentData] = useState(null);
  const [test, setTest] = useState(null);
  const [error, setError] = useState('');
  const [exhausted, setExhausted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const navigate = useNavigate();
  const { isSidebarOpen } = useSidebar();
  const sidebarWidth = isSidebarOpen ? '15.6rem' : '4.4rem';

  // Get stored student ID and check payment status on component mount
  useEffect(() => {
    const storedStudentId = localStorage.getItem('id');
    if (storedStudentId) {
      setStudentId(storedStudentId);
      checkStudentPaymentStatus(storedStudentId);
      
      // Try to get student data from localStorage
      const storedStudent = localStorage.getItem('student');
      if (storedStudent) {
        try {
          setStudentData(JSON.parse(storedStudent));
        } catch (e) {
          console.error("Error parsing student data:", e);
        }
      }
    }
  }, []);

  // Function to check payment status when component loads
  const checkStudentPaymentStatus = async (id) => {
    try {
      setLoading(true);
      const status = await checkPaymentStatus(id);
      
      console.log("Payment status:", status);
      
      setPaymentStatus(status);
      
      // Get student account type to determine if we need to show payment UI right away
      const storedStudent = localStorage.getItem('student');
      if (storedStudent) {
        try {
          const studentData = JSON.parse(storedStudent);
          setStudentData(studentData);
          
          // If portal-created account and no payment, show payment UI (no free tests)
          if (studentData.accountType === 'portalCreated' && (!status || !status.isPaid || status.isExpired)) {
            setExhausted(true);
            setError("Payment required to access tests. No free tests are available.");
          }
          // If admin-created account with expired payment
          else if (status?.isExpired) {
            setExhausted(true);
            setError("Your test access has expired. Please renew to continue.");
          }
        } catch (e) {
          console.error("Error handling student data:", e);
        }
      }
    } catch (err) {
      console.error("Error checking payment status:", err);
      // Don't set error here as it might not be an actual error
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!studentId.trim()) {
      setError("Please enter your Student ID");
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Store ID in localStorage for persistence
      localStorage.setItem('id', studentId); 
      
      const result = await generateTest(studentId);
      console.log("Generate Test Response:", result);

      if (result?.paymentRequired || result?.message?.includes("Payment required")) {
        setExhausted(true);
        setError(result.message || "Payment required to access tests.");
      } else {
        setTest(result);
        setError('');
        setExhausted(false);
      }
    } catch (err) {
      console.error("Generate test error:", err);
      if (err.response?.status === 402 || err.message?.includes("Payment required") || err.message?.includes("exhausted")) {
        // Payment required status code
        setExhausted(true);
        setError(err.response?.data?.message || 'Payment required to access tests');
      } else {
        setError(err.message || 'Failed to generate test');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = () => {
    navigate(`/take-test/${test.testId}`, {
      state: { studentId },
    });
  };

  const loadRazorpay = (src) =>
    new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const handlePayment = async () => {
    if (!studentId.trim()) {
      setError("Please enter your Student ID before making a payment");
      return;
    }

    setLoading(true);
    try {
      const res = await loadRazorpay('https://checkout.razorpay.com/v1/checkout.js');

      if (!res) {
        alert('Failed to load Razorpay SDK');
        setLoading(false);
        return;
      }

      // Call backend to create order
      const orderDetails = await createRazorpayOrder(studentId, 4999);
      console.log("Order created:", orderDetails);

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID, // Replace with your actual key
        amount: orderDetails.amount,
        currency: orderDetails.currency || 'INR',
        order_id: orderDetails.orderId,
        name: 'Exam Portal',
        description: 'Payment for Test Access',
        handler: async (response) => {
          try {
            setLoading(true);
            console.log("Payment successful response:", response);
            
            // Verify payment with the backend
            const verificationResult = await verifyRazorpayPayment(
              response.razorpay_order_id, 
              response.razorpay_payment_id,
              studentId // Pass studentId to associate payment with student
            );

            if (verificationResult.success) {
              alert('Payment Successful');
              setExhausted(false);
              setPaymentStatus({
                isPaid: true,
                isExpired: false,
                expiryDate: verificationResult.expiryDate || new Date(Date.now() + 30*24*60*60*1000).toISOString()
              });

              // Try to generate the test again
              const result = await generateTest(studentId);
              setTest(result);
            } else {
              alert('Payment verification failed: ' + (verificationResult.message || "Unknown error"));
            }
          } catch (err) {
            console.error("Payment verification error:", err);
            alert('Payment verification failed. Please try again.');
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name: studentData?.fullName || '',
          email: studentData?.email || '',
          contact: studentData?.mobile || '',
        },
        theme: {
          color: '#3399cc',
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
          }
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (err) {
      console.error("Payment creation error:", err);
      alert('Something went wrong with the payment: ' + (err.message || "Unknown error"));
      setLoading(false);
    }
  };

  // Check if the student is a portal-created account without payment
  const shouldShowPaymentPrompt = () => {
    if (!studentData) return false;
    
    // Portal-created accounts without payment OR any account with exhausted status
    return (studentData.accountType === 'portalCreated' && (!paymentStatus || !paymentStatus.isPaid || paymentStatus.isExpired)) || exhausted;
  };

  const paymentNoticeMessage = () => {
    if (!studentData) return "Payment required to access tests.";
    
    if (studentData.accountType === 'portalCreated') {
      return "Payment required to access tests. No free tests are available.";
    } else {
      return "You've used your 2 free tests. Please pay to continue.";
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f0f4f8', fontFamily: 'Segoe UI, sans-serif' }}>
      <div style={{ position: 'fixed', top: 0, left: 0, height: '100%', zIndex: 10 }}>
        <StudentSidebar />
      </div>

      <div
        style={{
          marginLeft: sidebarWidth,
          width: `calc(100% - ${sidebarWidth})`,
          transition: 'all 0.3s ease',
        }}
      >
        <Navbar />

        <div style={{ padding: '2rem', paddingTop: '4rem', maxWidth: '600px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: '600', marginBottom: '1.5rem', color: '#1e293b' }}>
            Generate Test
          </h2>

          {/* Payment Status Display */}
          {paymentStatus && paymentStatus.isPaid && !paymentStatus.isExpired && (
            <div style={{ 
              marginBottom: '1.5rem', 
              padding: '0.75rem', 
              backgroundColor: '#d1fae5', 
              borderRadius: '6px', 
              display: 'flex',
              alignItems: 'center'
            }}>
              <span style={{ color: '#047857', fontWeight: '500' }}>
                ✓ Premium access active until: {new Date(paymentStatus.expiryDate).toLocaleDateString()}
              </span>
            </div>
          )}

          {loading && (
            <div style={{ 
              marginBottom: '1.5rem', 
              padding: '0.75rem', 
              backgroundColor: '#f0f9ff', 
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center'
            }}>
              <span style={{ color: '#0369a1', fontWeight: '500' }}>
                ⟳ Loading, please wait...
              </span>
            </div>
          )}

          <label htmlFor="studentId" style={{ display: 'block', fontSize: '0.95rem', color: '#334155', marginBottom: '0.5rem' }}>
            Student ID
          </label>
          <input
            id="studentId"
            type="text"
            value={studentId}
            onChange={(e) => setStudentId(e.target.value)}
            placeholder="Enter your Student ID"
            style={{
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              padding: '0.6rem 0.75rem',
              width: '100%',
              marginBottom: '0.75rem',
              fontSize: '1rem',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />

          {/* Don't show Generate button for portal-created accounts without payment */}
          {!shouldShowPaymentPrompt() && (
            <button 
              onClick={handleGenerate} 
              style={{...buttonStyle, opacity: loading ? 0.7 : 1}}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Build Test'}
            </button>
          )}

          {error && (
            <p style={{ color: 'red', marginTop: '1.25rem', fontSize: '0.95rem' }}>
              {error}
            </p>
          )}

          {test && (
            <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#e0f2fe', borderRadius: '8px' }}>
              <p style={{ marginBottom: '0.75rem', color: '#0369a1', fontWeight: '500' }}>
                ✅ Test generated successfully!
              </p>
              <button onClick={handleStartTest} style={buttonStyle}>
                Start Test
              </button>
            </div>
          )}

          {shouldShowPaymentPrompt() && (
            <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#fee2e2', borderRadius: '8px' }}>
              <p style={{ marginBottom: '0.75rem', color: '#b91c1c', fontWeight: '500' }}>
                ❌ {paymentNoticeMessage()}
              </p>
              <button 
                onClick={handlePayment} 
                style={{...buttonStyle, opacity: loading ? 0.7 : 1}}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Pay ₹49.99'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const buttonStyle = {
  backgroundColor: '#3b82f6',
  color: '#fff',
  padding: '0.6rem 1.25rem',
  borderRadius: '6px',
  border: 'none',
  fontSize: '1rem',
  fontWeight: '500',
  cursor: 'pointer',
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
  transition: 'background-color 0.3s',
};

export default GenerateTestPage;